import { Button, Checkbox as MuiCheckbox, CheckboxProps, styled, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Typography } from '../../../components/Typography/Typography';
import { AvatarCell } from '../../../components/Table/CellRenderers/AvatarCell';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { useIcons } from '../../../hooks/useIcons';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close-blue.svg';
import { ApprovalDelegate, ApprovalDelegatePerson } from '../../../queries/hooks/useApprovalDelegates';

export interface OwnerGroup {
  owner: ApprovalDelegatePerson;
  /** Delegated rows for this owner, already filtered by search/OP filters. */
  rows: ApprovalDelegate[];
  /** Total delegated rows for this owner (unfiltered). */
  delegatedCount: number;
  /** Total (deal × OP) responsibilities for this owner (unfiltered). */
  totalCount: number;
  /** False when the owner has no remaining (deal, OP) pair left to delegate. */
  canDelegate: boolean;
}

interface Props {
  groups: OwnerGroup[];
  selectedIds: Set<number>;
  onToggleRow: (id: number) => void;
  onToggleManyRows: (ids: number[], select: boolean) => void;
  onRemoveRow: (row: ApprovalDelegate) => void;
  onDelegateOwner: (ownerId: number) => void;
}

/** Checkbox matching the Accountant Timesheet Statuses table (default MUI size + indeterminate). */
function TableCheckbox({ indeterminate, ...rest }: { indeterminate?: boolean } & CheckboxProps) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const ref = useRef<HTMLInputElement>(null!);
  const { colors } = useTheme();
  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate, rest.checked]);
  return (
    <MuiCheckbox
      inputRef={ref}
      indeterminate={indeterminate}
      inputMode='text'
      sx={{
        color: colors.border.default,
        '&.Mui-checked': { color: colors.textAccent.default },
      }}
      {...rest}
    />
  );
}

/**
 * Grouped, collapsible, selectable table of approval delegations. Level 1 lists EVERY deal owner (even
 * with zero delegations) with a per-owner Delegate action; expanding shows that owner's delegations.
 * Styling mirrors the shared TableV1 tables (header cells, 48px rows, column dividers, zebra striping
 * starting on bg2, row hover) while owner rows use the accent/selection blue from the mockup.
 */
export const DelegationsTable = ({
  groups,
  selectedIds,
  onToggleRow,
  onToggleManyRows,
  onRemoveRow,
  onDelegateOwner,
}: Props) => {
  const { colors } = useTheme();
  const { ArrowDownIcon, ArrowRightIcon, PlusIcon } = useIcons();
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  const toggleCollapse = (ownerId: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(ownerId) ? next.delete(ownerId) : next.add(ownerId);
      return next;
    });
  };

  if (groups.length === 0) {
    return (
      <EmptyState style={{ border: `1px solid ${colors.border.default}` }}>
        <Typography variant='body' color={colors.text.secondary}>
          No deal owners to display.
        </Typography>
      </EmptyState>
    );
  }

  // Header checkbox selects every currently-visible (filtered) delegated row across all groups.
  const allVisibleIds = groups.flatMap((g) => g.rows.map((r) => r.id));
  const selectedVisible = allVisibleIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected = allVisibleIds.length > 0 && selectedVisible === allVisibleIds.length;

  // Running index across all rendered data rows → stable zebra striping.
  let dataRowIndex = -1;

  return (
    <TableWrapper style={{ border: `1px solid ${colors.border.default}` }}>
      <HeaderRow
        style={{ backgroundColor: colors.surfaceBackground.bg1, borderBottom: `1px solid ${colors.border.default}` }}
      >
        <Cell $center>
          <TableCheckbox
            checked={allVisibleSelected}
            indeterminate={selectedVisible > 0 && !allVisibleSelected}
            disabled={allVisibleIds.length === 0}
            onChange={() => onToggleManyRows(allVisibleIds, !allVisibleSelected)}
          />
        </Cell>
        <Cell $center />
        <Cell>
          <TableHeaderCell text='Deal Owner / Deal' />
        </Cell>
        <Cell>
          <TableHeaderCell text='Operating Partner' />
        </Cell>
        <Cell>
          <TableHeaderCell text='Delegated To' />
        </Cell>
        <Cell $center>
          <TableHeaderCell text='Action' />
        </Cell>
      </HeaderRow>

      {groups.map((group) => {
        const groupRowIds = group.rows.map((r) => r.id);
        const selectedCount = groupRowIds.filter((id) => selectedIds.has(id)).length;
        const allSelected = groupRowIds.length > 0 && selectedCount === groupRowIds.length;
        const isExpanded = !collapsed.has(group.owner.id);
        return (
          <div key={group.owner.id}>
            <GroupRow
              style={{
                backgroundColor: colors.surfaceStatus.noAction,
                borderBottom: `1px solid ${colors.border.default}`,
              }}
            >
              <Cell $center>
                <TableCheckbox
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  disabled={groupRowIds.length === 0}
                  onChange={() => onToggleManyRows(groupRowIds, !allSelected)}
                />
              </Cell>
              <Cell $center>
                <ChevronButton onClick={() => toggleCollapse(group.owner.id)} aria-label='Toggle'>
                  {isExpanded ? <ArrowDownIcon /> : <ArrowRightIcon />}
                </ChevronButton>
              </Cell>
              <Cell style={{ gridColumn: '3 / 6', cursor: 'pointer' }} onClick={() => toggleCollapse(group.owner.id)}>
                <AvatarCell name={group.owner.name} avatarLetterStyle={{ color: colors.text.main }} />
                <CountPill style={{ backgroundColor: colors.surfaceBackground.bg1 }}>
                  <Typography variant='caption' color={colors.text.caption}>
                    <strong>{group.delegatedCount}</strong>
                    {`/${group.totalCount} delegated`}
                  </Typography>
                </CountPill>
              </Cell>
              <Cell $center>
                <Button
                  variant='text'
                  disabled={!group.canDelegate}
                  onClick={() => onDelegateOwner(group.owner.id)}
                  style={{
                    height: '28px',
                    backgroundColor: colors.surfaceBackground.bg1,
                    border: `1px solid ${colors.border.default}`,
                  }}
                  startIcon={<PlusIcon />}
                  sx={{
                    '.MuiButton-startIcon': { marginRight: '2px' },
                    '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' },
                    svg: { g: { path: { fill: colors.icon.accent } } },
                  }}
                >
                  <Typography variant='h4' color={colors.textAccent.default} style={{ marginBottom: '-2px' }}>
                    Delegate
                  </Typography>
                </Button>
              </Cell>
            </GroupRow>

            {isExpanded &&
              group.rows.map((row) => {
                dataRowIndex += 1;
                const isSelected = selectedIds.has(row.id);
                const zebra =
                  dataRowIndex % 2 === 0 ? colors.surfaceBackground.bg2 : colors.surfaceBackground.bg1;
                return (
                  <DataRow
                    key={row.id}
                    style={{
                      backgroundColor: isSelected ? colors.surfaceInteraction.selectedLight : zebra,
                      borderBottom: `1px solid ${colors.border.default}`,
                    }}
                  >
                    <Cell $center>
                      <TableCheckbox checked={isSelected} onChange={() => onToggleRow(row.id)} />
                    </Cell>
                    <Cell $center />
                    <Cell style={{ paddingLeft: '12px' }}>
                      <Typography variant='body' color={colors.text.main}>
                        {row.deal.name}
                      </Typography>
                    </Cell>
                    <Cell>
                      <AvatarCell name={row.operatingPartner.name} avatarLetterStyle={{ color: colors.text.main }} />
                    </Cell>
                    <Cell>
                      <AvatarCell name={row.toDealOwner.name} avatarLetterStyle={{ color: colors.text.main }} />
                    </Cell>
                    <Cell $center>
                      <Button
                        variant='text'
                        onClick={() => onRemoveRow(row)}
                        aria-label='Remove delegation'
                        style={{
                          height: '28px',
                          backgroundColor: colors.surfaceBackground.bg1,
                          border: `1px solid ${colors.border.default}`,
                        }}
                        startIcon={<CloseIcon />}
                        sx={{
                          '.MuiButton-startIcon': { marginRight: '2px' },
                          svg: { path: { fill: colors.icon.accent } },
                        }}
                      >
                        <Typography variant='h4' color={colors.textAccent.default} style={{ marginBottom: '-2px' }}>
                          Remove
                        </Typography>
                      </Button>
                    </Cell>
                  </DataRow>
                );
              })}
          </div>
        );
      })}
    </TableWrapper>
  );
};

const COLUMNS = '56px 48px 2fr 2fr 2fr 140px';

const TableWrapper = styled('div')`
  border-radius: 4px;
  overflow: hidden;
`;

const BaseRow = styled('div')`
  display: grid;
  grid-template-columns: ${COLUMNS};
  align-items: stretch;
  min-height: 48px;
  & > div:not(:last-of-type) {
    border-right: 1px solid ${({ theme }) => theme.colors.border.default};
  }
`;

const HeaderRow = styled(BaseRow)`
  min-height: 36px;
`;

const GroupRow = styled(BaseRow)`
  min-height: 56px;
`;

const DataRow = styled(BaseRow)`
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceBackground.highlighted} !important;
  }
`;

const Cell = styled('div')<{ $center?: boolean }>`
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};
  min-width: 0;
  p {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ChevronButton = styled('button')`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
`;

const CountPill = styled('div')`
  padding: 2px 10px;
  border-radius: 12px;
  white-space: nowrap;
`;

const EmptyState = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  border-radius: 4px;
`;
